import React, {useState} from "react"
import {optionalHorizontalField} from "../HorizontalField/HorizontalField"
import {isMasked, Permissible} from "../../resources/common"
import {
	Editable,
	EditableElementProps,
	EditableHiddenString,
	EditablePassport,
	EditableSSN,
} from "../HorizontalField/EditableHorizontalField"
import {isUndefined} from "lodash"
import {useIsUnitUser} from "../../services/auth"
import IdentificationTypeSelect from "../IdentificationTypeSelect/ApplicationIdentificationNumber"

export type SSNProps = {
	ssn: string
	nationality: string
}

interface Props {
	data: {
		initialSsn: SSNProps
		initialPassport: [string, string]
		initialMatriculaConsular: string
		documentTitle: string | undefined
		archived: boolean
	}
	state: {
		isEditing: string | undefined
		isEditable: boolean
		isUpdating: boolean
		isHiddenIconVisible?: boolean
	}
	actions: {
		setIsEditing: Function
		refresh: () => void
		setIsUpdating: Function
		updateSSN: Function
		updatePassport: Function
		updateMatriculaConsular: Function
	}
}

export default function IdentificationNumber(props: Props) {
	const {initialSsn, initialPassport, initialMatriculaConsular, documentTitle, archived} = props.data
	const {isEditing, isEditable, isUpdating, isHiddenIconVisible} = props.state
	const {setIsEditing, refresh, setIsUpdating, updateSSN, updatePassport, updateMatriculaConsular} = props.actions

	const [ssnProps, setSsnProps] = useState<SSNProps>(initialSsn)
	const [matriculaConsular, setMatriculaConsular] = useState(initialMatriculaConsular)
	const [passport, setPassport] = useState<[string, string]>(initialPassport)

	const [currentEditingType, setCurrentEditingType] = useState(getEditingType(props))

	const getEditingComp = (editingType: string) => {
		if (editingType == "ssn") {
			return getEditSSNComp()
		}
		if (editingType == "passport") {
			return getEditPassportComp()
		}

		if (editingType == "matriculaConsular") {
			return getEditMatriculaConsularComp()
		}

		return undefined
	}

	const getEditSSNComp = () => (
		<Editable
			label={"SSN"}
			isEditing={isEditing === documentTitle}
			isDisabled={!isUndefined(isEditing)}
			isAllowed={useIsUnitUser() && isEditable}
			isUpdating={isUpdating}
			isHiddenIconVisible={isHiddenIconVisible}
			onStartEdit={() => setIsEditing(documentTitle)}
			onCancel={refresh}
			setValue={setSsnProps}
			value={ssnProps}
			initial={ssnProps}
			update={() => {
				updateSSN(ssnProps)
				setIsUpdating(true)
			}}
			className={archived ? "archived-field" : ""}
		>
			{(props: EditableElementProps<any>) => (
				<>
					{getSelectIdTypeComp()}
					{EditableSSN(props)}
				</>
			)}
		</Editable>
	)

	const getEditMatriculaConsularComp = () => (
		<Editable
			label={"Matricula Consular"}
			isEditing={isEditing === documentTitle}
			isDisabled={!isUndefined(isEditing)}
			isAllowed={useIsUnitUser() && isEditable}
			isUpdating={isUpdating}
			onStartEdit={() => setIsEditing(documentTitle)}
			onCancel={refresh}
			setValue={setMatriculaConsular}
			value={matriculaConsular}
			initial={matriculaConsular}
			update={() => {
				updateMatriculaConsular(matriculaConsular)
				setIsUpdating(true)
			}}
			className={archived ? "archived-field" : ""}
		>
			{(props: EditableElementProps<any>) => (
				<>
					{getSelectIdTypeComp()}
					{EditableHiddenString(props)}
				</>
			)}
		</Editable>
	)

	const getEditPassportComp = () => (
		<Editable
			label={"Passport"}
			isEditing={isEditing === documentTitle}
			isDisabled={!isUndefined(isEditing)}
			isAllowed={useIsUnitUser() && isEditable}
			isUpdating={isUpdating}
			onStartEdit={() => setIsEditing(documentTitle)}
			onCancel={refresh}
			setValue={setPassport}
			value={passport}
			initial={passport}
			update={() => {
				updatePassport(passport)
				setIsUpdating(true)
			}}
			className={archived ? "archived-field" : ""}
		>
			{(props) => (
				<>
					{getSelectIdTypeComp()}
					{EditablePassport(props)}
				</>
			)}
		</Editable>
	)

	const getSelectIdTypeComp = () =>
		isEditing == "SSN" || isEditing == "Passport" || isEditing == "Matricula Consular" ? (
			<IdentificationTypeSelect value={currentEditingType} onChange={(e) => setCurrentEditingType(e.target.value)} />
		) : (
			<></>
		)

	function renderSSN() {
		if (!ssnProps || documentTitle !== "SSN") {
			return null
		}
		return isMasked(ssnProps.ssn) ? (
			optionalHorizontalField(
				documentTitle,
				`●● ${(ssnProps.ssn || "").replace(/[*]/g, "●")}`,
				undefined,
				archived ? "archived-field" : ""
			)
		) : (
			<>{getEditingComp(currentEditingType)}</>
		)
	}

	function renderMatriculaConsular() {
		if (!matriculaConsular || documentTitle !== "Matricula Consular") {
			return null
		}
		return isMasked(matriculaConsular) ? (
			optionalHorizontalField(
				documentTitle,
				(matriculaConsular || "").replace(/[*]/g, "●"),
				undefined,
				archived ? "archived-field" : ""
			)
		) : (
			<>{getEditingComp(currentEditingType)}</>
		)
	}

	function renderPassport() {
		if (!passport || documentTitle !== "Passport") {
			return null
		}
		return isMasked(passport[1]) ? (
			optionalHorizontalField(
				documentTitle,
				`●● ${(passport[1] || "").replace(/[*]/g, "●")}`,
				undefined,
				archived ? "archived-field" : ""
			)
		) : (
			<>{getEditingComp(currentEditingType)}</>
		)
	}

	return renderSSN() || renderPassport() || renderMatriculaConsular()
}

function getEditingType(props: Props) {
	if (props.data.documentTitle == "Passport") {
		return "passport"
	}
	if (props.data.documentTitle == "Matricula Consular") {
		return "matriculaConsular"
	}
	return "ssn"
}

export function getDocumentTitle(
	ssn: Permissible<string>,
	maskedSSN: Permissible<string>,
	passport: Permissible<string>,
	maskedPassport: Permissible<string>,
	matriculaConsular: Permissible<string>,
	maskedMatriculaConsular: Permissible<string>
) {
	if (matriculaConsular || maskedMatriculaConsular) {
		return "Matricula Consular"
	}
	if (passport || maskedPassport) {
		return "Passport"
	}
	if (ssn || maskedSSN) {
		return "SSN"
	}
	return undefined
}
